import { createTheme, Theme } from '@material-ui/core/styles';

// Color manipulation utilities
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Calculate relative luminance to determine if color is light or dark
const getRelativeLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex);
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map(c => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Check if color is light or dark
export const isLightColor = (hex: string): boolean => {
  return getRelativeLuminance(hex) > 0.5;
};

export const adjustBrightness = (hex: string, factor: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    Math.min(255, Math.max(0, Math.round(r * (1 + factor / 100)))),
    Math.min(255, Math.max(0, Math.round(g * (1 + factor / 100)))),
    Math.min(255, Math.max(0, Math.round(b * (1 + factor / 100))))
  );
};

const adjustSaturation = (hex: string, factor: number): string => {
  const [r, g, b] = hexToRgb(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const saturation = max === 0 ? 0 : delta / max;
  const newSaturation = Math.max(0, Math.min(1, saturation * (1 + factor / 100)));

  if (newSaturation === 0) {
    const gray = Math.round(max * 0.299 + g * 0.587 + b * 0.114);
    return rgbToHex(gray, gray, gray);
  }

  const newDelta = newSaturation * max;
  const newMin = max - newDelta;

  const newR = r === max ? max : r === min ? newMin : r * (newDelta / delta) + newMin;
  const newG = g === max ? max : g === min ? newMin : g * (newDelta / delta) + newMin;
  const newB = b === max ? max : b === min ? newMin : b * (newDelta / delta) + newMin;

  return rgbToHex(Math.round(newR), Math.round(newG), Math.round(newB));
};

export const createAlphaColor = (hex: string, alpha: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Modern primary color - can be light or dark
const PRIMARY_COLOR = '#95c93d'; // Modern green

// Generate a complete modern theme from primary color
const generateModernTheme = (primaryColor: string, isDark: boolean) => {
  // Detect if primary color is light or dark
  const isPrimaryLight = isLightColor(primaryColor);

  // Primary color variations
  const primary = primaryColor;
  const primaryLight = isPrimaryLight ? adjustBrightness(primary, 20) : adjustBrightness(primary, 30);
  const primaryDark = isPrimaryLight ? adjustBrightness(primary, -30) : adjustBrightness(primary, -20);
  const primaryLighter = isPrimaryLight ? adjustBrightness(primary, 40) : adjustBrightness(primary, 50);
  const primaryDarker = isPrimaryLight ? adjustBrightness(primary, -50) : adjustBrightness(primary, -40);

  // Background colors - adapt based on primary color brightness
  const backgroundBase = isDark ? '#0F172A' : '#FFFFFF';

  // For dark theme: if primary is light, use it more prominently; if dark, use it subtly
  // For light theme: if primary is light, use it subtly; if dark, use it more prominently
  const background = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, -70) // Light primary in dark theme - use more prominently
      : adjustBrightness(primary, -85) // Dark primary in dark theme - use subtly
    : backgroundBase;

  const surface = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, -60) // Light primary in dark theme
      : adjustBrightness(primary, -75) // Dark primary in dark theme
    : isPrimaryLight
      ? adjustBrightness(primary, 90) // Light primary in light theme - use subtly
      : adjustBrightness(primary, 95); // Dark primary in light theme - use more prominently

  // Text colors - always black for light theme, white/black for dark theme based on primary color
  const textPrimary = isDark ? (isPrimaryLight ? '#000000' : '#FFFFFF') : '#000000';
  const textSecondary = isDark ? (isPrimaryLight ? '#666666' : '#CCCCCC') : '#666666';

  // Border colors - adapt based on primary color brightness
  const border = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, -50) // Light primary in dark theme
      : adjustBrightness(primary, -60) // Dark primary in dark theme
    : isPrimaryLight
      ? adjustBrightness(primary, 80) // Light primary in light theme
      : adjustBrightness(primary, 85); // Dark primary in light theme

  // Semantic colors - adapt based on primary color brightness
  const error = isDark
    ? isPrimaryLight
      ? adjustSaturation(primary, 30) // Light primary - less saturation needed
      : adjustSaturation(primary, 40) // Dark primary - more saturation
    : isPrimaryLight
      ? adjustSaturation(primary, 50) // Light primary - more saturation needed
      : adjustSaturation(primary, 60); // Dark primary - more saturation

  const success = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, 15) // Light primary - subtle adjustment
      : adjustBrightness(primary, 20) // Dark primary - more adjustment
    : isPrimaryLight
      ? adjustBrightness(primary, -15) // Light primary - subtle adjustment
      : adjustBrightness(primary, -20); // Dark primary - more adjustment

  const warning = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, 8) // Light primary - subtle adjustment
      : adjustBrightness(primary, 10) // Dark primary - more adjustment
    : isPrimaryLight
      ? adjustBrightness(primary, -8) // Light primary - subtle adjustment
      : adjustBrightness(primary, -10); // Dark primary - more adjustment

  const info = isDark
    ? isPrimaryLight
      ? adjustBrightness(primary, 12) // Light primary - subtle adjustment
      : adjustBrightness(primary, 15) // Dark primary - more adjustment
    : isPrimaryLight
      ? adjustBrightness(primary, -12) // Light primary - subtle adjustment
      : adjustBrightness(primary, -15); // Dark primary - more adjustment

  // Disabled colors - ensure good contrast
  const disabled = isDark
    ? isPrimaryLight
      ? adjustBrightness(textPrimary, -30) // Light primary - darker disabled
      : adjustBrightness(textPrimary, -40) // Dark primary - darker disabled
    : isPrimaryLight
      ? adjustBrightness(textPrimary, 30) // Light primary - lighter disabled
      : adjustBrightness(textPrimary, 40); // Dark primary - lighter disabled

  const disabledBackground = isDark
    ? isPrimaryLight
      ? adjustBrightness(surface, -8) // Light primary - subtle background
      : adjustBrightness(surface, -10) // Dark primary - more background
    : isPrimaryLight
      ? adjustBrightness(surface, 8) // Light primary - subtle background
      : adjustBrightness(surface, 10); // Dark primary - more background

  return {
    primary,
    primaryLight,
    primaryDark,
    primaryLighter,
    primaryDarker,
    background,
    surface,
    text: textPrimary,
    textSecondary,
    border,
    error,
    success,
    warning,
    info,
    disabled,
    disabledBackground,
    isPrimaryLight, // Add this for reference
  };
};

// Create theme config with dynamic primary color
const createThemeConfig = (primaryColor: string) => ({
  // Primary color from props
  primaryColor,

  // Light theme colors derived from primary
  light: generateModernTheme(primaryColor, false),

  // Dark theme colors derived from primary
  dark: generateModernTheme(primaryColor, true),

  // Modern gradients derived from primary color
  gradients: {
    primary: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustBrightness(primaryColor, 30)} 100%)`,
    primaryHover: `linear-gradient(135deg, ${adjustBrightness(primaryColor, 20)} 0%, ${adjustBrightness(primaryColor, 50)} 100%)`,
    secondary: `linear-gradient(135deg, ${adjustBrightness(primaryColor, -20)} 0%, ${adjustBrightness(primaryColor, -40)} 100%)`,
    background: `linear-gradient(135deg, ${generateModernTheme(primaryColor, false).background} 0%, ${generateModernTheme(primaryColor, false).surface} 100%)`,
    backgroundDark: `linear-gradient(135deg, ${generateModernTheme(primaryColor, true).background} 0%, ${generateModernTheme(primaryColor, true).surface} 100%)`,
  },

  // Modern typography
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: {
      xs: '10px',
      sm: '12px',
      base: '14px',
      lg: '16px',
      xl: '18px',
      '2xl': '22px',
      '3xl': '30px',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Modern spacing
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
  },

  // Modern border radius
  borderRadius: {
    none: '0px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },

  // Modern transitions
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },

  // Modern shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
});

// Initialize with default primary color
let AUTH_THEME_CONFIG = createThemeConfig('#95c93d');

// Function to update theme config with new primary color
export const updateThemeConfig = (primaryColor: string): void => {
  try {
    AUTH_THEME_CONFIG = createThemeConfig(primaryColor);
  } catch (error) {
    console.warn('Failed to update theme config:', error);
  }
};

// Helper function to get theme colors
export const getThemeColors = (theme: 'light' | 'dark') => {
  return AUTH_THEME_CONFIG[theme];
};

// Helper function to get gradient
export const getGradient = (type: keyof typeof AUTH_THEME_CONFIG.gradients) => {
  return AUTH_THEME_CONFIG.gradients[type];
};

// Create modern Material-UI theme
export const createAuthTheme = (theme: 'light' | 'dark', primaryColor?: string): Theme => {
  // Update config if primary color is provided
  if (primaryColor) {
    updateThemeConfig(primaryColor);
  }

  const colors = getThemeColors(theme);

  return createTheme({
    palette: {
      type: theme,
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      secondary: {
        main: colors.primaryLight,
        light: colors.primaryLighter,
        dark: colors.primaryDark,
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      error: {
        main: colors.error,
        light: adjustBrightness(colors.error, 20),
        dark: adjustBrightness(colors.error, -20),
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      success: {
        main: colors.success,
        light: adjustBrightness(colors.success, 20),
        dark: adjustBrightness(colors.success, -20),
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      warning: {
        main: colors.warning,
        light: adjustBrightness(colors.warning, 20),
        dark: adjustBrightness(colors.warning, -20),
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      info: {
        main: colors.info,
        light: adjustBrightness(colors.info, 20),
        dark: adjustBrightness(colors.info, -20),
        contrastText: theme === 'dark' ? colors.background : colors.surface,
      },
      text: {
        primary: colors.text,
        secondary: colors.textSecondary,
        disabled: colors.disabled,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      divider: colors.border,
      action: {
        disabled: colors.disabled,
        disabledBackground: colors.disabledBackground,
      },
    },
    typography: {
      fontFamily: AUTH_THEME_CONFIG.typography.fontFamily,
      h1: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize['2xl'],
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.bold,
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.tight,
      },
      h2: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize['xl'],
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.semibold,
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.tight,
      },
      h3: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize.lg,
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.normal,
      },
      body1: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.normal,
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.normal,
      },
      body2: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.normal,
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.normal,
      },
      button: {
        fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
        fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
        textTransform: 'none',
        lineHeight: AUTH_THEME_CONFIG.typography.lineHeight.tight,
      },
    },
    shape: {
      borderRadius: parseInt(AUTH_THEME_CONFIG.borderRadius.lg),
    },
    spacing: (factor: number) => `${8 * factor}px`,
    overrides: {
      MuiButton: {
        root: {
          borderRadius: AUTH_THEME_CONFIG.borderRadius.lg,
          transition: AUTH_THEME_CONFIG.transitions.normal,
          textTransform: 'none',
          fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
          padding: '12px 24px',
          fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
        },
        contained: {
          boxShadow: AUTH_THEME_CONFIG.shadows.md,
          '&:hover': {
            boxShadow: AUTH_THEME_CONFIG.shadows.lg,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            transform: 'translateY(-1px)',
          },
        },
        text: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
      },
      MuiTextField: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: AUTH_THEME_CONFIG.borderRadius.lg,
            transition: AUTH_THEME_CONFIG.transitions.normal,
            fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
            color: colors.text,
          },
          '& .MuiOutlinedInput-root input': {
            color: colors.text,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.text,
            borderWidth: '2px',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.text,
            borderWidth: '2px',
          },
          '& .MuiInputLabel-root': {
            fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
            fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
            color: colors.textSecondary,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.text,
          },
          // Autofill transparency
          '& .MuiOutlinedInput-root input:-webkit-autofill': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset',
            '-webkit-text-fill-color': 'inherit',
            'transition': 'background-color 5000s ease-in-out 0s',
            'background-color': 'transparent',
          },
          '& .MuiOutlinedInput-root input:-webkit-autofill:hover': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset',
            '-webkit-text-fill-color': 'inherit',
            'transition': 'background-color 5000s ease-in-out 0s',
            'background-color': 'transparent',
          },
          '& .MuiOutlinedInput-root input:-webkit-autofill:focus': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset',
            '-webkit-text-fill-color': 'inherit',
            'transition': 'background-color 5000s ease-in-out 0s',
            'background-color': 'transparent',
          },
          '& .MuiOutlinedInput-root input:-webkit-autofill:active': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset',
            '-webkit-text-fill-color': 'inherit',
            'transition': 'background-color 5000s ease-in-out 0s',
            'background-color': 'transparent',
          },
          // Override Material-UI's specific autofill classes
          '& .MuiOutlinedInput-input:-webkit-autofill': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset !important',
            '-webkit-text-fill-color': `${colors.text} !important`,
            'transition': 'background-color 5000s ease-in-out 0s !important',
            'background-color': 'transparent !important',
          },
          '& .MuiOutlinedInput-input:-webkit-autofill:hover': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset !important',
            '-webkit-text-fill-color': `${colors.text} !important`,
            'transition': 'background-color 5000s ease-in-out 0s !important',
            'background-color': 'transparent !important',
          },
          '& .MuiOutlinedInput-input:-webkit-autofill:focus': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset !important',
            '-webkit-text-fill-color': `${colors.text} !important`,
            'transition': 'background-color 5000s ease-in-out 0s !important',
            'background-color': 'transparent !important',
          },
          '& .MuiOutlinedInput-input:-webkit-autofill:active': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset !important',
            '-webkit-text-fill-color': `${colors.text} !important`,
            'transition': 'background-color 5000s ease-in-out 0s !important',
            'background-color': 'transparent !important',
          },
          // Target any Material-UI autofill class with higher specificity
          '& [class*="MuiOutlinedInput-input"]:-webkit-autofill': {
            '-webkit-box-shadow': '0 0 0 30px transparent inset !important',
            '-webkit-text-fill-color': `${colors.text} !important`,
            'transition': 'background-color 5000s ease-in-out 0s !important',
            'background-color': 'transparent !important',
          },
        },
      },
      MuiPaper: {
        root: {
          borderRadius: AUTH_THEME_CONFIG.borderRadius.xl,
          boxShadow: AUTH_THEME_CONFIG.shadows.lg,
        },
      },
      MuiTypography: {
        root: {
          color: colors.text,
        },
        h1: {
          color: colors.text,
        },
        h2: {
          color: colors.text,
        },
        h3: {
          color: colors.text,
        },
        h4: {
          color: colors.text,
        },
        h5: {
          color: colors.text,
        },
        h6: {
          color: colors.text,
        },
        body1: {
          color: colors.text,
        },
        body2: {
          color: colors.textSecondary,
        },
      },
      MuiLink: {
        root: {
          color: colors.primary,
          textDecoration: 'none',
          fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
          transition: AUTH_THEME_CONFIG.transitions.normal,
          '&:hover': {
            color: colors.primaryLight,
            textDecoration: 'underline',
          },
        },
      },
      MuiFormControl: {
        root: {
          '& .MuiInputLabel-root': {
            fontSize: AUTH_THEME_CONFIG.typography.fontSize.base,
            fontWeight: AUTH_THEME_CONFIG.typography.fontWeight.medium,
          },
        },
      },
    },
  });
};
